import DOMPurify from "dompurify";

export interface LogoBackground {
  type: "transparent" | "solid" | "gradient";
  color: string;
  endColor: string;
  angle: number;
}
export interface LogoEdits {
  removeBackground: boolean;
  color: string;
  tolerance: number;
  cropTransparent: boolean;
}
export type LogoPlacement = "card" | "banner";
export type LogoTarget = "desktop" | "mobile";

export const DEFAULT_BACKGROUND: LogoBackground = { type: "transparent", color: "#ffffff", endColor: "#0065dd", angle: 90 };
export const DEFAULT_EDITS: LogoEdits = { removeBackground: false, color: "#ffffff", tolerance: 10, cropTransparent: false };

export interface ClientLogoVariant {
  source: string;
  image: string;
  fileName: string;
  width: number;
  height: number;
  zoom: number;
  x: number;
  y: number;
  /** Independent deformation. Missing values in legacy records mean 1. */
  stretchX?: number;
  stretchY?: number;
  background?: LogoBackground;
  edits?: LogoEdits;
}

export interface ClientLogo extends ClientLogoVariant {
  enabled: boolean;
  /** Legacy values without placement are rendered in the original card location. */
  placement?: LogoPlacement;
  /** Optional dedicated mobile artwork. Desktop is used as a responsive fallback. */
  mobile?: ClientLogoVariant;
}

export const LOGO_WIDTH = 640;
export const LOGO_HEIGHT = 240;
export const MAX_LOGO_BYTES = 5 * 1024 * 1024;
export const MAX_SOURCE_DIMENSION = 4096;
export const MAX_SOURCE_PIXELS = 16_000_000;

export const LOGO_PROFILES: Record<LogoPlacement, Record<LogoTarget, { width: number; height: number; label: string }>> = {
  card: {
    desktop: { width: 640, height: 240, label: "640 × 240 px" },
    mobile: { width: 640, height: 240, label: "640 × 240 px" },
  },
  banner: {
    desktop: { width: 1920, height: 500, label: "1920 × 500 px" },
    mobile: { width: 800, height: 600, label: "800 × 600 px" },
  },
};

export const logoProfile = (placement: LogoPlacement = "card", target: LogoTarget = "desktop") =>
  LOGO_PROFILES[placement][target];

export function loadLogoImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("No pudimos leer la imagen. Prueba con otro archivo."));
    image.src = src;
  });
}

export function logoQuality(width: number, height: number, zoom: number, placement: LogoPlacement = "card", target: LogoTarget = "desktop",
  stretchX = 1, stretchY = 1): string | null {
  const profile = logoProfile(placement, target);
  const scale = (placement === "banner"
    ? Math.max(profile.width / width, profile.height / height)
    : Math.min(profile.width / width, profile.height / height)) * zoom;
  return scale * Math.max(stretchX, stretchY) > 1
    ? "Resolución baja para este tamaño. Puede verse pixelado; reduce el tamaño o usa una imagen más grande."
    : null;
}

// The source is immutable. Pixel edits are always recomputed from it, never from a previous export.
const preparedImages = new WeakMap<HTMLImageElement, { key: string; canvas: HTMLCanvasElement }>();
export function prepareLogo(image: HTMLImageElement, edits: LogoEdits = DEFAULT_EDITS): HTMLCanvasElement {
  const key = JSON.stringify(edits);
  const cached = preparedImages.get(image);
  if (cached?.key === key) return cached.canvas;
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth; canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d")!;
  context.drawImage(image, 0, 0);
  if (edits.removeBackground || edits.cropTransparent) {
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    const { data, width, height } = pixels;
    if (edits.removeBackground) {
      const color = [1, 3, 5].map((offset) => parseInt(edits.color.slice(offset, offset + 2), 16));
      const threshold = (edits.tolerance / 100) ** 2 * 3 * 255 ** 2;
      const visited = new Uint8Array(width * height);
      const queue = new Int32Array(width * height);
      let head = 0, tail = 0;
      const visit = (index: number) => {
        if (visited[index]) return;
        visited[index] = 1;
        const offset = index * 4;
        const distance = color.reduce((sum, channel, i) => sum + (data[offset + i] - channel) ** 2, 0);
        if (data[offset + 3] === 0 || distance <= threshold) queue[tail++] = index;
      };
      for (let x = 0; x < width; x++) { visit(x); visit((height - 1) * width + x); }
      for (let y = 0; y < height; y++) { visit(y * width); visit(y * width + width - 1); }
      while (head < tail) {
        const index = queue[head++];
        data[index * 4 + 3] = 0;
        if (index % width > 0) visit(index - 1);
        if (index % width < width - 1) visit(index + 1);
        if (index >= width) visit(index - width);
        if (index < width * (height - 1)) visit(index + width);
      }
      context.putImageData(pixels, 0, 0);
    }
    if (edits.cropTransparent) {
      let left = width, top = height, right = -1, bottom = -1;
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 0) {
          left = Math.min(left, x); right = Math.max(right, x);
          top = Math.min(top, y); bottom = Math.max(bottom, y);
        }
      }
      if (right < left) throw new Error("La edición dejó la imagen completamente transparente. Reduce la tolerancia o restaura el original.");
      const cropped = context.getImageData(left, top, right - left + 1, bottom - top + 1);
      canvas.width = cropped.width; canvas.height = cropped.height;
      context.putImageData(cropped, 0, 0);
    }
  }
  preparedImages.set(image, { key, canvas });
  return canvas;
}

type LogoSettings = Pick<ClientLogoVariant, "zoom" | "x" | "y" | "stretchX" | "stretchY" | "background" | "edits">;
export interface LogoLayout {
  outputWidth: number;
  outputHeight: number;
  imageLeft: number;
  imageTop: number;
  imageWidth: number;
  imageHeight: number;
  visibleLeft: number;
  visibleTop: number;
  visibleWidth: number;
  visibleHeight: number;
}

/**
 * Calculates the artwork bounds inside the maximum editing frame. Published
 * banners crop the unused vertical area, so scaling a banner down also brings
 * the form content up. The fixed 500/600 px frame is kept only in edit mode.
 */
export function calculateLogoLayout(preparedWidth: number, preparedHeight: number, logo: LogoSettings,
  placement: LogoPlacement = "card", target: LogoTarget = "desktop", compactBanner = false): LogoLayout {
  const profile = logoProfile(placement, target);
  const scale = (placement === "banner"
    ? Math.max(profile.width / preparedWidth, profile.height / preparedHeight)
    : Math.min(profile.width / preparedWidth, profile.height / preparedHeight)) * logo.zoom;
  const imageWidth = preparedWidth * scale * (logo.stretchX ?? 1);
  const imageHeight = preparedHeight * scale * (logo.stretchY ?? 1);
  const fixedLeft = (profile.width - imageWidth) / 2 + logo.x;
  const fixedTop = (profile.height - imageHeight) / 2 + logo.y;
  const visibleLeft = Math.max(0, fixedLeft);
  const visibleRight = Math.min(profile.width, fixedLeft + imageWidth);
  const visibleTop = Math.max(0, fixedTop);
  const visibleBottom = Math.min(profile.height, fixedTop + imageHeight);
  const visibleWidth = Math.max(1, visibleRight - visibleLeft);
  const visibleHeight = Math.max(1, visibleBottom - visibleTop);
  const shouldCompact = compactBanner && placement === "banner";
  const outputHeight = shouldCompact ? Math.max(1, Math.min(profile.height, Math.round(visibleHeight))) : profile.height;

  return {
    outputWidth: profile.width,
    outputHeight,
    imageLeft: fixedLeft,
    imageTop: shouldCompact ? fixedTop - visibleTop : fixedTop,
    imageWidth,
    imageHeight,
    visibleLeft,
    visibleTop,
    visibleWidth,
    visibleHeight,
  };
}

export function drawLogo(canvas: HTMLCanvasElement, image: HTMLImageElement, logo: LogoSettings,
  placement: LogoPlacement = "card", target: LogoTarget = "desktop", options: { compactBanner?: boolean } = {}): LogoLayout {
  const prepared = prepareLogo(image, logo.edits);
  const layout = calculateLogoLayout(prepared.width, prepared.height, logo, placement, target, options.compactBanner);
  const { outputWidth, outputHeight } = layout;
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Tu navegador no permite ajustar imágenes.");
  context.clearRect(0, 0, outputWidth, outputHeight);
  const background = logo.background || DEFAULT_BACKGROUND;
  if (background.type !== "transparent") {
    context.fillStyle = background.color;
    if (background.type === "gradient") {
      // Match CSS angles: 0 points up, 90 points right.
      const radians = background.angle * Math.PI / 180;
      const dx = Math.sin(radians), dy = -Math.cos(radians);
      const length = Math.abs(outputWidth * dx) + Math.abs(outputHeight * dy);
      const gradient = context.createLinearGradient(outputWidth / 2 - dx * length / 2, outputHeight / 2 - dy * length / 2,
        outputWidth / 2 + dx * length / 2, outputHeight / 2 + dy * length / 2);
      gradient.addColorStop(0, background.color); gradient.addColorStop(1, background.endColor);
      context.fillStyle = gradient;
    }
    context.fillRect(0, 0, outputWidth, outputHeight);
  }
  context.drawImage(prepared, layout.imageLeft, layout.imageTop, layout.imageWidth, layout.imageHeight);
  return layout;
}

export function renderLogo(image: HTMLImageElement, settings: LogoSettings,
  placement: LogoPlacement = "card", target: LogoTarget = "desktop"): string {
  const canvas = document.createElement("canvas");
  drawLogo(canvas, image, settings, placement, target, { compactBanner: placement === "banner" });
  const output = canvas.toDataURL("image/png");
  if (output.length > MAX_LOGO_BYTES * 4 / 3 + 22) {
    throw new Error("La imagen procesada supera los 5 MB. Reduce el tamaño o usa una imagen más liviana.");
  }
  return output;
}

export async function readClientLogoVariant(file: File, placement: LogoPlacement = "card", target: LogoTarget = "desktop"): Promise<ClientLogoVariant> {
  if (!/\.(png|jpe?g|svg|webp)$/i.test(file.name)) throw new Error("Usa una imagen PNG, JPG, SVG o WebP.");
  if (file.size > 5 * 1024 * 1024) throw new Error("La imagen supera los 5 MB. Usa una versión más liviana.");
  let blob: Blob = file;
  const vector = /\.svg$/i.test(file.name);
  if (vector) {
    const safeSvg = DOMPurify.sanitize(await file.text(), {
      USE_PROFILES: { svg: true, svgFilters: true },
      FORBID_TAGS: ["script", "foreignObject", "image", "use", "style", "a", "animate", "set"],
      FORBID_ATTR: ["href", "xlink:href", "style"],
    });
    const doc = new DOMParser().parseFromString(safeSvg, "image/svg+xml");
    const root = doc.documentElement;
    if (root.localName !== "svg" || doc.querySelector("parsererror")) throw new Error("El SVG no es válido.");
    // Rasterize vectors at a useful resolution, including viewBox-only files.
    const viewBox = root.getAttribute("viewBox")?.trim().split(/[\s,]+/).map(Number);
    const ratio = viewBox?.length === 4 && viewBox[2] > 0 && viewBox[3] > 0
      ? viewBox[2] / viewBox[3]
      : (parseFloat(root.getAttribute("width") || "640") / parseFloat(root.getAttribute("height") || "240"));
    if (!Number.isFinite(ratio) || ratio <= 0) throw new Error("El SVG no tiene dimensiones válidas.");
    root.setAttribute("width", String(Math.round(Math.min(1280, 1280 * ratio))));
    root.setAttribute("height", String(Math.round(Math.min(1280, 1280 / ratio))));
    blob = new Blob([new XMLSerializer().serializeToString(root)], { type: "image/svg+xml" });
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = await loadLogoImage(url);
    if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > MAX_SOURCE_PIXELS) {
      throw new Error("La imagen tiene dimensiones no admitidas. Usa una versión de hasta 16 megapíxeles.");
    }
    let canvas = document.createElement("canvas");
    const scale = Math.min(1, MAX_SOURCE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext("2d")!.drawImage(image, 0, 0, canvas.width, canvas.height);
    let source = canvas.toDataURL("image/png");
    // PNG can be larger than an uploaded JPG/WebP. Reduce it progressively instead of
    // rejecting a valid <=5 MiB upload because of our internal normalization format.
    while (source.length > MAX_LOGO_BYTES * 4 / 3 + 22 && canvas.width > 320 && canvas.height > 120) {
      const ratio = Math.min(.92, Math.sqrt((MAX_LOGO_BYTES * 4 / 3) / source.length) * .95);
      const smaller = document.createElement("canvas");
      smaller.width = Math.max(1, Math.floor(canvas.width * ratio));
      smaller.height = Math.max(1, Math.floor(canvas.height * ratio));
      smaller.getContext("2d")!.drawImage(canvas, 0, 0, smaller.width, smaller.height);
      canvas = smaller;
      source = canvas.toDataURL("image/png");
    }
    if (source.length > MAX_LOGO_BYTES * 4 / 3 + 22) throw new Error("La imagen procesada supera los 5 MB. Usa una versión más liviana.");
    // Exact-size banners should fill their standard canvas; card logos keep a little breathing room.
    const settings = { zoom: placement === "banner" ? 1 : 0.85, x: 0, y: 0, stretchX: 1, stretchY: 1 };
    return {
      source, image: renderLogo(await loadLogoImage(source), settings, placement, target),
      fileName: file.name.slice(0, 160), width: canvas.width, height: canvas.height, ...settings,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function readClientLogo(file: File, placement: LogoPlacement = "card", target: LogoTarget = "desktop"): Promise<ClientLogo> {
  return { enabled: true, placement, ...(await readClientLogoVariant(file, placement, target)) };
}
